(function () {
  if (window.__stayaiFreeGiftLoaded) return;
  window.__stayaiFreeGiftLoaded = true;

  const STORAGE_KEY = 'stayai_free_gift_registry';
  const registry = new Map(); // productId(string) -> { variantId, titles: [t1,t2,t3] }

  let syncing = false; // true while WE are writing to the cart (ignore our own resulting events)

  /* ---------------------------------------------------------------- */
  /* Registry: which products have a configured free gift              */
  /* ---------------------------------------------------------------- */

  function loadCachedRegistry() {
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      Object.entries(cached).forEach(([productId, config]) => registry.set(productId, config));
    } catch (e) {
      /* ignore malformed cache */
    }
  }

  function persistRegistry() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(registry)));
    } catch (e) {
      /* ignore quota errors */
    }
  }

  function collectRegistryFromDom() {
    let changed = false;
    document.querySelectorAll('purchase-options[data-free-gift-variant-id]').forEach((el) => {
      const productId = el.getAttribute('data-free-gift-product-id');
      const variantId = el.getAttribute('data-free-gift-variant-id');
      if (!productId || !variantId) return;

      const config = {
        variantId: String(variantId),
        titles: [
          el.getAttribute('data-free-gift-title-1') || '',
          el.getAttribute('data-free-gift-title-2') || '',
          el.getAttribute('data-free-gift-title-3') || '',
        ],
      };
      const existing = registry.get(String(productId));
      if (!existing || JSON.stringify(existing) !== JSON.stringify(config)) {
        registry.set(String(productId), config);
        changed = true;
      }
    });
    if (changed) persistRegistry();
  }

  // Line item properties for the gift line - just the 3 titles
  function giftProperties(config) {
    return {
      'Free gift 1': config.titles[0],
      'Free gift 2': config.titles[1],
      'Free gift 3': config.titles[2],
    };
  }

  function registeredGiftVariantIds() {
    return new Set(Array.from(registry.values()).map((c) => c.variantId));
  }

  /* ---------------------------------------------------------------- */
  /* Cart API helpers                                                   */
  /* ---------------------------------------------------------------- */

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function writeCart(url, body) {
    syncing = true;
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    } finally {
      syncing = false;
    }
  }

  function changeLine(key, quantity) {
    return writeCart('/cart/change.js', { id: key, quantity });
  }

  function addLine(variantId, quantity, properties) {
    return writeCart('/cart/add.js', { items: [{ id: Number(variantId), quantity, properties }] });
  }

  async function refreshUpcartUI() {
    if (typeof window.upcartRefreshCart !== 'function') return;
    try {
      await window.upcartRefreshCart();
    } catch (err) {
      console.error('[stayai-free-gift] upcartRefreshCart failed', err);
    }
  }

  let giftQueue = Promise.resolve();

  function handleAddedToCart(item) {
    if (!item || syncing) return;

    const productId = String(item.productId);
    const config = registry.get(productId);
    if (!config) return; // not a product we manage a gift for
    if (!item.sellingPlanAllocation) return; // only gift subscribed purchases

    const qtyAdded = item.quantityAdded;
    if (!qtyAdded || qtyAdded <= 0) return;

    giftQueue = giftQueue.then(() => addGiftIfMissing(config));
    return giftQueue;
  }

  async function addGiftIfMissing(config) {
    try {
      const cart = await getCart();
      const giftVariantIds = registeredGiftVariantIds();
      const aGiftAlreadyInCart = (cart.items || []).some((line) =>
        giftVariantIds.has(String(line.variant_id))
      );
      console.log('[stayai-free-gift] add check', {
        giftVariantIds: Array.from(giftVariantIds),
        cartVariantIds: (cart.items || []).map((i) => i.variant_id),
        aGiftAlreadyInCart,
      });
      if (aGiftAlreadyInCart) return;

      await addLine(config.variantId, 1, giftProperties(config));
      await refreshUpcartUI();
    } catch (err) {
      console.error('[stayai-free-gift] gift add failed', err);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Cleanup path: remove gift(s) once no registered subscription        */
  /* remains in the cart                                                 */
  /* ---------------------------------------------------------------- */

  let cleaning = false;

  async function cleanupGiftsIfNoSubscription() {
    if (syncing || cleaning) return;
    cleaning = true;
    try {
      const cart = await getCart();
      const items = cart.items || [];

      const hasActiveRegisteredSubscription = items.some(
        (item) => item.selling_plan_allocation && registry.has(String(item.product_id))
      );
      const giftVariantIds = registeredGiftVariantIds();
      const giftLines = items.filter((item) => giftVariantIds.has(String(item.variant_id)));
      console.log("testing");
      console.log('[stayai-free-gift] cleanup check', {
        items: items.map((i) => ({
          product_id: i.product_id,
          variant_id: i.variant_id,
          selling_plan_allocation: i.selling_plan_allocation,
        })),
        hasActiveRegisteredSubscription,
        giftLinesFound: giftLines.length,
      });
      if (hasActiveRegisteredSubscription) return; // still have a subscribed product, keep the gift

      if (giftLines.length === 0) return;

      for (const line of giftLines) {
        await changeLine(line.key, 0);
      }
      await refreshUpcartUI();
    } catch (err) {
      console.error('[stayai-free-gift] gift cleanup failed', err);
    } finally {
      cleaning = false;
    }
  }

  /* ---------------------------------------------------------------- */
  /* UpCart event wiring                                                */
  /* ---------------------------------------------------------------- */

  function wireUpcartEvents() {
    if (typeof window.upcartSubscribeAddedToCart === 'function') {
      window.upcartSubscribeAddedToCart((event) => {
        handleAddedToCart(event && event.item);
      });
    } else {
      console.warn('[stayai-free-gift] window.upcartSubscribeAddedToCart is not available yet');
    }

    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(() => {
        cleanupGiftsIfNoSubscription();
      });
    } else {
      console.warn('[stayai-free-gift] window.upcartSubscribeCartUpdated is not available yet');
    }
  }

  /* ---------------------------------------------------------------- */
  /* Boot                                                                */
  /* ---------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', () => {
    loadCachedRegistry();
    collectRegistryFromDom();
    wireUpcartEvents();
    cleanupGiftsIfNoSubscription(); 
  });
})();
